import { FilterOutlined } from "@ant-design/icons";
import { Select } from "antd";
import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { Product } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { patchProductsSelectedFilters } from "@/store/slices/ui";

import { FastFilterWrapper, StyledSelect } from "./Styled";

const { Option } = Select;

export interface DropdownFiltersProps {
    products: Record<string, Product>;
    onFilteredChange: (filtered: Product[]) => void;
    resetKey?: never;
}

const DropdownFilters: FC<DropdownFiltersProps> = ({
    products,
    onFilteredChange,
}) => {
    const dispatch = useAppDispatch();
    const isFirstMount = useRef(true);
    const { selectedFilters } = useAppSelector(
        (state) => state.ui.productsFilter,
    );

    const staticOptions = useMemo(() => {
        const groupSet = new Set<string>();
        const detailSet = new Set<string>();
        Object.values(products).forEach((p) => {
            const g = p.params.fin_group?.value;
            if (g !== undefined) {
                if (Array.isArray(g)) g.forEach((v) => groupSet.add(String(v)));
                else groupSet.add(String(g));
            }
            const d = p.params.fin_group_detail?.value;
            if (d !== undefined) {
                if (Array.isArray(d))
                    d.forEach((v) => detailSet.add(String(v)));
                else detailSet.add(String(d));
            }
        });
        return {
            fin_group: Array.from(groupSet).sort((a, b) =>
                a.localeCompare(b, "ua", { sensitivity: "base" }),
            ),
            fin_group_detail: Array.from(detailSet).sort((a, b) =>
                a.localeCompare(b, "ua", { sensitivity: "base" }),
            ),
        };
    }, [products]);

    const filteredProducts = useMemo(() => {
        const groupVals =
            (selectedFilters.fin_group as Array<string | number>) || [];
        const detailVals =
            (selectedFilters.fin_group_detail as Array<string | number>) || [];
        let arr = Object.values(products);

        if (groupVals.length) {
            arr = arr.filter((p) => {
                const v = p.params.fin_group?.value;
                return Array.isArray(v)
                    ? v.some((x) => groupVals.includes(x))
                    : groupVals.includes(v as never);
            });
        } else if (detailVals.length) {
            arr = arr.filter((p) => {
                const v = p.params.fin_group_detail?.value;
                return Array.isArray(v)
                    ? v.some((x) => detailVals.includes(x))
                    : detailVals.includes(v as never);
            });
        }

        return arr;
    }, [products, selectedFilters]);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }

        onFilteredChange(filteredProducts);
    }, [filteredProducts, onFilteredChange]);

    const onGroupChange = useCallback(
        async (values) => {
            dispatch(patchProductsSelectedFilters({}));
            dispatch(
                patchProductsSelectedFilters({
                    fin_group: values ? [values] : [],
                }),
            );
            dispatch(patchProductsSelectedFilters({ fin_group_detail: [] }));
        },
        [dispatch],
    );

    const onDetailChange = useCallback(
        async (values) => {
            dispatch(patchProductsSelectedFilters({}));
            dispatch(
                patchProductsSelectedFilters({
                    fin_group_detail: values ? [values] : [],
                }),
            );
            dispatch(patchProductsSelectedFilters({ fin_group: [] }));
        },
        [dispatch],
    );

    return (
        <FastFilterWrapper>
            <StyledSelect
                onChange={onGroupChange}
                value={selectedFilters.fin_group || []}
                listHeight={400}
                allowClear
                size="large"
                style={{ width: "45%" }}
                popupMatchSelectWidth={false}
                styles={{
                    popup: {
                        root: {
                            width: "92%",
                        },
                    },
                }}
                placeholder={
                    <React.Fragment>
                        <FilterOutlined />
                        &nbsp; Група
                    </React.Fragment>
                }
            >
                {staticOptions.fin_group.map((opt) => (
                    <Option key={opt} value={opt}>
                        {opt}
                    </Option>
                ))}
            </StyledSelect>
            <StyledSelect
                value={selectedFilters.fin_group_detail || []}
                onChange={onDetailChange}
                listHeight={400}
                allowClear
                size="large"
                style={{ width: "50%" }}
                popupMatchSelectWidth={false}
                styles={{
                    popup: {
                        root: {
                            width: "92%",
                        },
                    },
                }}
                placeholder={
                    <React.Fragment>
                        <FilterOutlined />
                        &nbsp; Детальна група
                    </React.Fragment>
                }
            >
                {staticOptions.fin_group_detail.map((opt) => (
                    <Option key={opt} value={opt}>
                        {opt}
                    </Option>
                ))}
            </StyledSelect>
        </FastFilterWrapper>
    );
};

export default DropdownFilters;
